import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Container,
  Stepper,
  Step,
  StepLabel,
  Grid,
  MenuItem,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import api from '../../utils/api';

const steps = ['Business Information', 'Admin Account'];

// Business categories will be loaded dynamically from API
const defaultBusinessCategories = [
  { code: 'healthcare', name: 'Healthcare & Pharmacy', description: 'Pharmacies, clinics, medical practices, healthcare providers' },
  { code: 'retail', name: 'Retail Store', description: 'General retail, clothing, electronics, books, consumer goods' },
  { code: 'food_beverage', name: 'Food & Beverage', description: 'Restaurants, cafes, bars, food trucks, catering services' },
  { code: 'beauty_wellness', name: 'Beauty & Wellness', description: 'Salons, spas, fitness centers, beauty services' },
  { code: 'automotive', name: 'Automotive', description: 'Auto parts, car dealerships, repair shops, automotive services' },
  { code: 'professional', name: 'Professional Services', description: 'Consulting, legal, accounting, real estate, professional services' },
  { code: 'entertainment', name: 'Entertainment', description: 'Gaming, events, recreation, entertainment venues' },
  { code: 'other', name: 'Other', description: 'Other business types not listed in the categories above' }
];

const Register = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [businessCategories, setBusinessCategories] = useState(defaultBusinessCategories);
  const [, setCategoriesLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const passwordInputRef = useRef(null);
  const confirmPasswordInputRef = useRef(null);
  const [formData, setFormData] = useState({
    // Business Information
    businessName: '',
    businessType: '',
    businessEmail: '',
    businessPhone: '',
    businessAddress: '',
    // Admin Account
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
  });
  const [acceptTerms, setAcceptTerms] = useState(false);

  // Load business categories from API
  useEffect(() => {
    const loadBusinessCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await api.get('/business-categories');
        if (response.data.success && response.data.categories) {
          setBusinessCategories(response.data.categories);
        }
      } catch (error) {
        // Keep default categories if API fails
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadBusinessCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateBusinessInfo = () => {
    if (!formData.businessName) return 'Business name is required';
    if (!formData.businessType) return 'Business type is required';
    if (!formData.businessEmail) return 'Business email is required';
    if (!formData.businessPhone) return 'Business phone is required';
    if (!formData.businessAddress) return 'Business address is required';
    return '';
  };

  const validateAdminInfo = () => {
    if (!formData.firstName) return 'First name is required';
    if (!formData.lastName) return 'Last name is required';
    if (!formData.email) return 'Email is required';
    if (!formData.password) return 'Password is required';
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    if (formData.password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    return '';
  };

  const handleNext = () => {
    const error = activeStep === 0 ? validateBusinessInfo() : validateAdminInfo();
    if (error) {
      setError(error);
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
    setError('');
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptTerms) {
      setError('You must accept the terms and conditions to register.');
      return;
    }
    const error = validateAdminInfo();
    if (error) {
      setError(error);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/auth/register', {
        business: {
          name: formData.businessName,
          type: formData.businessType,
          email: formData.businessEmail,
          phone: formData.businessPhone,
          address: formData.businessAddress,
        },
        admin: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phoneNumber: formData.phoneNumber,
        },
        acceptTerms,
      });

      setSuccess(true);
      
      // Redirect to login after 10 seconds
      setTimeout(() => {
        navigate('/login');
      }, 10000);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = (field) => {
    const input = field === 'password' ? passwordInputRef.current : confirmPasswordInputRef.current;
    const cursorPosition = input?.selectionStart || 0;
    
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }

    // Use setTimeout to ensure the cursor is set after the input type changes
    setTimeout(() => {
      if (input) {
        input.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const renderBusinessForm = () => (
    <Grid container spacing={2}>
      <Grid size={12}>
        <TextField
          required
          fullWidth
          label="Business Name"
          name="businessName"
          placeholder="Enter your business name"
          value={formData.businessName}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={12}>
        <FormControl fullWidth required>
          <InputLabel>Business Type</InputLabel>
          <Select
            name="businessType"
            value={formData.businessType}
            onChange={handleChange}
            label="Business Type"
            sx={{
              minHeight: '56px'
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300
                }
              }
            }}
          >
            {businessCategories.map((category) => (
              <MenuItem key={category.code} value={category.code}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {category.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {category.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          required
          fullWidth
          label="Business Email"
          name="businessEmail"
          type="email"
          placeholder="business@company.com"
          value={formData.businessEmail}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          required
          fullWidth
          label="Business Phone"
          name="businessPhone"
          placeholder="(123) 456-7890"
          value={formData.businessPhone}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={12}>
        <TextField
          required
          fullWidth
          label="Business Address"
          name="businessAddress"
          placeholder="123 Main St, City, State, ZIP"
          multiline
          rows={2}
          value={formData.businessAddress}
          onChange={handleChange}
        />
      </Grid>
    </Grid>
  );

  const renderAdminForm = () => (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          required
          fullWidth
          label="First Name"
          name="firstName"
          placeholder="Enter your first name"
          value={formData.firstName}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          required
          fullWidth
          label="Last Name"
          name="lastName"
          placeholder="Enter your last name"
          value={formData.lastName}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={12}>
        <TextField
          required
          fullWidth
          label="Email Address"
          name="email"
          type="email"
          placeholder="your@email.com"
          value={formData.email}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={12}>
        <TextField
          required
          fullWidth
          label="Phone Number"
          name="phoneNumber"
          placeholder="(123) 456-7890"
          value={formData.phoneNumber}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={12}>
        <TextField
          required
          fullWidth
          label="Password"
          name="password"
          placeholder="Create a strong password (8+ characters)"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange}
          inputRef={passwordInputRef}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Show/Hide Password">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => handleClickShowPassword('password')}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid size={12}>
        <TextField
          required
          fullWidth
          label="Confirm Password"
          name="confirmPassword"
          placeholder="Re-enter your password"
          type={showConfirmPassword ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={handleChange}
          inputRef={confirmPasswordInputRef}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Show/Hide Password">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => handleClickShowPassword('confirmPassword')}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={acceptTerms}
              onChange={e => setAcceptTerms(e.target.checked)}
              required
            />
          }
          label={
            <span>
              I accept the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions and Privacy Policy</a>
            </span>
          }
        />
      </Grid>
    </Grid>
  );

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            Registration successful! Please check your email to verify your account.
          </Alert>
          <Typography variant="body1" align="center">
            A verification link has been sent to your email address. Please verify your email before logging in.
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary">
            Redirecting to login page in 10 seconds...
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'background.default',
        py: 12,
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 4,
            }}
          >
            <Typography
              component="h1"
              variant="h3"
              gutterBottom
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                mb: 1,
              }}
            >
              FlexPoS
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontWeight: 400,
                mb: 3,
              }}
            >
              Get started with your business
            </Typography>

            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{ width: '100%', mb: 4 }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              {activeStep === 0 ? renderBusinessForm() : renderAdminForm()}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  onClick={handleBack}
                  disabled={activeStep === 0 || loading}
                  variant="outlined"
                >
                  Back
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Create Account'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={loading}
                  >
                    Next
                  </Button>
                )}
              </Box>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component={RouterLink}
                  to="/login"
                  variant="body2"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Already have an account? Sign in
                </Link>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Register; 